import React, { useEffect, useState } from 'react';
import api from '../services/api';
import '../styles/VendorFleet.css';

interface Driver {
  _id: string;
  firstName: string;
  lastName: string;
  phone: string;
  vehicleType: string;
  vehiclePlate: string;
  isOnline: boolean;
  currentJobId?: string;
  totalJobs: number;
  rating: number;
  location?: string;
}

const VendorFleet: React.FC = () => {
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchDrivers();
  }, []);

  const fetchDrivers = async () => {
    setIsLoading(true);
    try {
      const data = await api.getVendorFleetDrivers();
      setDrivers(data.drivers);
      setError('');
    } catch (err: any) {
      setError('Failed to load fleet drivers');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveDriver = async (driverId: string, driverName: string) => {
    if (!window.confirm(`Are you sure you want to remove ${driverName} from your fleet?`)) {
      return;
    }

    try {
      await api.removeDriverFromFleet(driverId);
      fetchDrivers();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to remove driver');
    }
  };

  if (isLoading) {
    return (
      <div className="vendor-fleet-page">
        <div className="loading">Loading fleet drivers...</div>
      </div>
    );
  }

  return (
    <div className="vendor-fleet-page">
      <div className="page-header">
        <div>
          <h1>My Fleet</h1>
          <p>Manage your fleet drivers</p>
        </div>
        <div className="header-actions">
          <button className="refresh-button" onClick={fetchDrivers}>
            🔄 Refresh
          </button>
        </div>
      </div>

      {error && <div className="error-box">{error}</div>}

      <div className="enrollment-info">
        <h3>📱 How to Add Drivers to Your Fleet</h3>
        <p>
          Drivers can enroll in your fleet using the driver mobile app:
        </p>
        <ol>
          <li>Driver opens the mobile app and navigates to "Enroll with Vendor"</li>
          <li>Driver enters your phone number</li>
          <li>You receive an OTP on your phone</li>
          <li>Share the OTP with the driver to complete enrollment</li>
        </ol>
      </div>

      {/* Desktop Table */}
      <div className="desktop-table-container">
        <div className="table-container">
          <table className="fleet-table">
            <thead>
              <tr>
                <th>Status</th>
                <th>Driver</th>
                <th>Contact</th>
                <th>Vehicle</th>
                <th>Location</th>
                <th>Jobs</th>
                <th>Rating</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {drivers.map((driver) => (
                <tr key={driver._id}>
                  <td>
                    <span className={`status-badge ${driver.isOnline ? 'online' : 'offline'}`}>
                      {driver.isOnline ? '🟢 Online' : '⚫ Offline'}
                    </span>
                  </td>
                  <td>
                    <strong>{driver.firstName} {driver.lastName}</strong>
                    {driver.currentJobId && (
                      <div className="secondary-text">On a job</div>
                    )}
                  </td>
                  <td>{driver.phone}</td>
                  <td>
                    <div>{driver.vehicleType}</div>
                    <div className="secondary-text">{driver.vehiclePlate}</div>
                  </td>
                  <td>{driver.location || 'N/A'}</td>
                  <td>{driver.totalJobs || 0}</td>
                  <td>⭐ {driver.rating?.toFixed(1) || '5.0'}</td>
                  <td>
                    <button
                      onClick={() => handleRemoveDriver(driver._id, `${driver.firstName} ${driver.lastName}`)}
                      className="btn-remove"
                      disabled={!!driver.currentJobId}
                      title={driver.currentJobId ? 'Cannot remove driver with active job' : 'Remove from fleet'}
                    >
                      🗑️ Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {drivers.length === 0 && (
            <div className="empty-state">
              <p>No drivers in your fleet yet</p>
              <p className="empty-subtitle">Drivers can enroll using your phone number in the mobile app</p>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Cards */}
      <div className="mobile-card-list">
        {drivers.map((driver) => (
          <div key={driver._id} className="data-card">
            <div className="data-card-header">
              <div>
                <div className="data-card-title">
                  {driver.firstName} {driver.lastName}
                </div>
                <div className="data-card-subtitle">{driver.phone}</div>
              </div>
              <span className={`status-badge ${driver.isOnline ? 'online' : 'offline'}`}>
                {driver.isOnline ? '🟢' : '⚫'}
              </span>
            </div>

            <div className="data-card-body">
              <div className="data-card-row">
                <span className="data-card-label">Vehicle:</span>
                <span className="data-card-value">{driver.vehicleType}</span>
              </div>
              <div className="data-card-row">
                <span className="data-card-label">Plate:</span>
                <span className="data-card-value">{driver.vehiclePlate}</span>
              </div>
              <div className="data-card-row">
                <span className="data-card-label">Location:</span>
                <span className="data-card-value">{driver.location || 'N/A'}</span>
              </div>
              <div className="data-card-row">
                <span className="data-card-label">Jobs:</span>
                <span className="data-card-value">{driver.totalJobs || 0}</span>
              </div>
              <div className="data-card-row">
                <span className="data-card-label">Rating:</span>
                <span className="data-card-value">⭐ {driver.rating?.toFixed(1) || '5.0'}</span>
              </div>
              {driver.currentJobId && (
                <div className="data-card-row">
                  <span className="data-card-label">Status:</span>
                  <span className="data-card-value">On a job</span>
                </div>
              )}
            </div>

            <div className="data-card-actions">
              <button
                onClick={() => handleRemoveDriver(driver._id, `${driver.firstName} ${driver.lastName}`)}
                className="btn-remove-full"
                disabled={!!driver.currentJobId}
              >
                🗑️ Remove from Fleet
              </button>
            </div>
          </div>
        ))}

        {drivers.length === 0 && (
          <div className="empty-state">
            <p>No drivers in your fleet yet</p>
            <p className="empty-subtitle">Drivers can enroll using your phone number in the mobile app</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default VendorFleet;
